import type { TabProps, TabsProps, TabsActions } from '@mui/material';
import { Tab, Tabs } from '@mui/material';
import { isNotNullOrUndefined, isNullOrUndefined } from '@weft/utils';
import React, { useEffect } from 'react';

export type VyPageLayoutRequiredTabsProps = {
  tabs?: TabProps[];
  currentTab?: string;
};

export type VyPageLayoutTabsProps = TabsProps & VyPageLayoutRequiredTabsProps & {
  tabsAction?: React.ReactNode;
};

export const VyPageLayoutTabs: React.FC<VyPageLayoutTabsProps & { containerRef: React.RefObject<HTMLDivElement>; }> = 
  ({ tabs, currentTab, containerRef, centered = false, tabsAction, ...tabsProps }) => {
    const actionRef = useTabsIndicatorPageResizeUpdate(containerRef, tabs, currentTab);

    if (isNullOrUndefined(tabs) || isNullOrUndefined(currentTab)) {
      return null;
    }

    return (
      <Tabs
        centered={centered}
        value={currentTab}
        action={actionRef}
        {...tabsProps}
      >
        {tabs.map((tab, index) => (
          <Tab
            {...tab}
            key={index}
          />
        ))}

        {tabsAction}
      </Tabs>
    );
  };

function useTabsIndicatorPageResizeUpdate(containerRef: React.RefObject<HTMLDivElement>, tabs: TabProps[] | undefined, currentTab: string | undefined) {
  const actionRef = React.useRef<TabsActions | null>(null);

  useEffect(() => {
    const copiedContainerRef = { ...containerRef };

    if (isNullOrUndefined(tabs) || isNullOrUndefined(currentTab)) {
      return undefined;
    }

    const resizeObserver = new ResizeObserver(() => {
      if (actionRef.current && copiedContainerRef.current) {
        actionRef.current.updateIndicator();
      }
    });

    if (copiedContainerRef.current) {
      resizeObserver.observe(copiedContainerRef.current);
    }

    return () => {
      isNotNullOrUndefined(copiedContainerRef.current) && resizeObserver.unobserve(copiedContainerRef.current);
    };
  }, [tabs, currentTab, containerRef]);

  return actionRef;
}